import { ArrowRight, Phone, MapPin } from "lucide-react";
import { ASSETS, CONTACT } from "@/lib/assets";

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-cyan/25 blur-[140px] pulse-soft" />
      <div className="absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full bg-coral/20 blur-[160px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mono text-[11px] uppercase tracking-[0.22em] text-white/70 mb-7 reveal"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
              Sveio · Vestland · Etablert håndverkerfirma
            </div>

            <h1 className="reveal text-[44px] sm:text-6xl lg:text-[80px] leading-[0.95] font-bold text-white" data-testid="hero-headline">
              Energi­oppgradering
              <br />
              <span className="text-gradient">som varer i tiår.</span>
            </h1>

            <p className="reveal mt-7 max-w-xl text-lg text-white/70 leading-relaxed" data-testid="hero-subhead">
              Vi servicer varmepumper, etterisolerer, kler om og bygger ut –
              med presisjon, lokal kompetanse og synlig kvalitet.
              <span className="text-cyan-soft"> Vi tar hele jobben.</span>
            </p>

            <div className="reveal mt-9 flex flex-wrap items-center gap-3" data-testid="hero-actions">
              <a href="#kontakt" className="btn btn-primary" data-testid="hero-cta-primary">
                Be om gratis befaring
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn btn-ghost" data-testid="hero-cta-call">
                <Phone className="w-4 h-4" />
                {CONTACT.phone}
              </a>
            </div>

            {/* Stats strip */}
            <div className="reveal mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg" data-testid="hero-stats">
              {[
                { k: "10+", v: "År erfaring" },
                { k: "100%", v: "Lokalt forankret" },
                { k: "6", v: "Tjenester" },
              ].map((s) => (
                <div key={s.v} className="border-l border-white/10 pl-4">
                  <div className="text-3xl sm:text-4xl font-bold text-white">{s.k}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              {/* Glow ring around image */}
              <div className="absolute -inset-3 rounded-3xl gradient-brand opacity-20 blur-2xl" />
              <div className="relative overflow-hidden rounded-3xl border hairline glow-cyan grain">
                <img
                  src={ASSETS.teamPhoto}
                  srcSet="/brand/team-sm.jpg 480w, /brand/team.jpg 720w"
                  sizes="(max-width: 768px) 90vw, 520px"
                  alt="Høie Service team på byggeplass"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-[440px] sm:h-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hs-bg))] via-transparent to-transparent" />
                {/* Floating chip */}
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between glass rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={ASSETS.logoIcon} alt="" className="w-9 h-9 spin-slow" />
                    <div>
                      <div className="text-sm font-semibold text-white">Pågående prosjekt</div>
                      <div className="mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /> Sveio
                      </div>
                    </div>
                  </div>
                  <a href="#tjenester" className="text-cyan text-sm font-semibold hover:underline">
                    Se mer →
                  </a>
                </div>
              </div>

              {/* Side floating badge */}
              <div className="hidden sm:flex absolute -left-6 top-10 glass rounded-2xl px-4 py-3 items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-cyan animate-pulse" />
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/70">
                  Tilgjengelig nå
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
